import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type { PrefetchedDataProps, PrefetchedUserDataProps } from '#src/shared/prefetch';
import { withPrefetchedData } from '#src/shared/prefetch';

import {
	type PetitionDetailsPagePrefetchedContext,
	type PetitionDetailsPagePrefetchedUserContext,
	prefetchPageContext,
	prefetchPageUserContext,
} from '#src/app/pages/petitionDetails/pageContext';
import type { Sharer } from '#src/app/pages/petitionDetails/shared/types';
import { isLoaded } from '#src/app/shared/utils/async';

import { getSharer } from '../api';
import { CommonPetitionDetails } from '../CommonPetitionDetails';
import {
	buildMetaContext,
	facebookSharerHeadline,
	petitionDetailsPageAlternateUrls,
	petitionDetailsPageCanonical,
	petitionDetailsPageMetadata,
	petitionDetailsPageSchemaOrg,
	petitionDetailsPageTitle,
} from '../shared';
import type { PetitionDetailsMetaContext } from '../shared';

import { getPetitionShareAssetAbImage } from './api/getPetitionShareAssetAbImage';

// EXPERIMENT: psf_share_asset_ab_experiment — the variant images are 1200x630 JPGs made by hand. At cleanup, keep the
// routes (links already shared keep pointing at them): with no image they serve the /bsv one.
const IMAGE_WIDTH = 1200;
const IMAGE_HEIGHT = 630;

type PrefetchedData = PetitionDetailsPagePrefetchedContext &
	Readonly<{
		sharer?: Sharer;
		imageUrl?: string;
		metaContext: PetitionDetailsMetaContext;
	}>;

/**
 * The crawler still gets a preview, so these failures are degraded, not broken. They are reported so the experiment's
 * health can be watched in Sentry.
 */
function reportWarning(utilityContext: UtilityContext, error: Error, params: Record<string, string>) {
	void utilityContext.errorReporter.report(
		{ error, context: { feature: 'psf_share_asset_ab_experiment' }, params },
		{ severity: 'warning' },
	);
}

async function getImageUrl(
	params: Readonly<{ slugOrId: string; experimentName: string; variant: string }>,
	utilityContext: UtilityContext,
): Promise<string | undefined> {
	try {
		return await getPetitionShareAssetAbImage(params, utilityContext);
	} catch (error) {
		reportWarning(
			utilityContext,
			new Error('Share asset A/B image fetch failed on the crawler route', { cause: error }),
			params,
		);
		return undefined;
	}
}

async function getSharerOrReport(
	slug: string,
	sharerUserId: string,
	utilityContext: UtilityContext,
): Promise<Sharer | undefined> {
	try {
		return await getSharer(sharerUserId, utilityContext);
	} catch (error) {
		reportWarning(
			utilityContext,
			new Error('Sharer fetch failed on the share asset A/B crawler route', { cause: error }),
			{ slug, sharerUserId },
		);
		return undefined;
	}
}

/**
 * Crawler route for the `/sa/:experimentName/:variant` share link segment. Serves the petition's variant image from the
 * psf_share_asset_ab FCM as og:image. For an unknown experiment name or variant, a petition no longer listed or a
 * failed fetch, it serves today's `/bsv` image instead, never an error page: channels cache the first preview they get.
 */
export const PetitionDetailsShareAssetAb = withPrefetchedData(
	function PetitionDetailsShareAssetAb({
		prefetchedData,
		prefetchedUserData,
	}: PrefetchedDataProps<PrefetchedData> & PrefetchedUserDataProps<PetitionDetailsPagePrefetchedUserContext>) {
		return <CommonPetitionDetails prefetchedData={prefetchedData} prefetchedUserData={prefetchedUserData} />;
	},
	{
		prefetchName: __MODULE_HASH__,
		prefetchData: async (context) => {
			const {
				params: { slug: slugParam, sharerUserId, experimentName, variant },
			} = context;
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			const slug = slugParam!;
			const [pageContext, imageUrl, sharer] = await Promise.all([
				prefetchPageContext(slug, context),
				experimentName && variant
					? getImageUrl({ slugOrId: slug, experimentName, variant }, context.utilityContext)
					: undefined,
				sharerUserId ? getSharerOrReport(slug, sharerUserId, context.utilityContext) : undefined,
			]);
			return {
				...pageContext,
				sharer,
				imageUrl,
				metaContext: buildMetaContext(context),
			};
		},
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		prefetchUserData: async (context) => prefetchPageUserContext(context.params.slug!, context),
		pageTitle: (ctx) => petitionDetailsPageTitle(ctx),
		pageCanonical: (ctx) => petitionDetailsPageCanonical(ctx),
		pageMetadata: ({ prefetchedData, i18n, getStaticAssetUrl }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			const { imageUrl } = prefetchedData;
			return petitionDetailsPageMetadata({
				prefetchedData,
				i18n,
				getStaticAssetUrl,
				metaContext: prefetchedData.metaContext,
				// the variant image replaces the /bsv one on these links, so the fallback is the /bsv image
				bestShareVisual: true,
				shareHeadlineOverrides: {
					...facebookSharerHeadline({
						pageData: prefetchedData.pageData.pageData,
						sharer: prefetchedData.sharer,
						i18n,
					}),
					...(imageUrl && { imageUrl, imageWidth: IMAGE_WIDTH, imageHeight: IMAGE_HEIGHT }),
				},
			});
		},
		pageSchemaOrg: (ctx) =>
			petitionDetailsPageSchemaOrg({
				prefetchedData: ctx.prefetchedData,
				resolveUrl: ctx.resolveUrl,
				defineArticle: ctx.defineArticle,
				defineBreadcrumb: ctx.defineBreadcrumb,
				definePerson: ctx.definePerson,
			}),
		pageAlternateUrls: (ctx) => petitionDetailsPageAlternateUrls(ctx),
	},
);
